<template>
  <div>
    <Navigation />
    <div class="pos">
      <div class="left">
        <div class="search-row">
          <input v-model="search" @input="onSearch" placeholder="Scan barcode or search product…" ref="scan" autofocus />
          <button class="secondary return-btn" @click="showReturn = true" v-if="can('sales.refund')" title="Process a return">
            <i class="mdi mdi-restore" style="margin-right:4px"></i>Return
          </button>
        </div>
        <div class="products-grid">
          <div v-for="v in filtered" :key="v.variant_id" class="prod-tile" @click="addToCart(v)">
            <div class="prod-name">{{ v.product_name }}</div>
            <div class="prod-variant">{{ v.variant_name }} &middot; {{ v.on_hand }} in stock</div>
            <div class="prod-price">KES {{ format(v.sell_price) }}</div>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="cart card">
          <div class="cart-head">
            <h3>Cart</h3>
            <div class="cust-picker">
              <div v-if="!attachedCustomer" class="cust-search">
                <input v-model="custSearch" @input="onCustSearch" placeholder="Attach customer (name or phone)…" />
                <div v-if="custResults.length" class="cust-results">
                  <div v-for="c in custResults" :key="c.id" class="cust-result" @click="pickCustomer(c)">
                    <strong>{{ c.name || 'Unnamed' }}</strong> <span class="muted small">· {{ c.phone || 'no phone' }} · {{ c.loyalty_points }} pts</span>
                  </div>
                </div>
              </div>
              <div v-else class="cust-chip">
                <i class="mdi mdi-account-check"></i>
                <strong>{{ attachedCustomer.name }}</strong>
                <span v-if="attachedCustomer.loyalty_points > 0" class="pts-badge">{{ attachedCustomer.loyalty_points }} pts</span>
                <button class="danger small" @click="clearCustomer">&times;</button>
              </div>
            </div>
          </div>
          <div v-if="!cart.length" class="empty">Cart is empty. Scan or tap items on the left.</div>
          <table v-else>
            <thead><tr><th>Item</th><th style="width:60px">Qty</th><th style="width:100px">Total</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in cart" :key="i">
                <td>
                  <div>{{ it.product_name }}</div>
                  <div class="muted small">{{ it.variant_name }} @ KES {{ format(it.unit_price) }}</div>
                </td>
                <td>
                  <input type="number" min="1" v-model.number="it.quantity" style="width:60px; padding: 5px" />
                </td>
                <td>KES {{ format(it.unit_price * it.quantity) }}</td>
                <td><button class="danger small" @click="cart.splice(i,1)">&times;</button></td>
              </tr>
            </tbody>
          </table>
          <div class="totals">
            <div class="row"><span>Subtotal</span><span>KES {{ format(subtotal) }}</span></div>
            <div class="row">
              <span>Code</span>
              <div style="display:flex; gap:4px; align-items:center;">
                <input v-model="discountCode" @blur="tryApplyCode" @keyup.enter="tryApplyCode" placeholder="e.g. WELCOME10" style="width:130px" />
                <button v-if="appliedCode" class="danger small" @click="clearCode" title="Remove code">&times;</button>
              </div>
            </div>
            <div v-if="appliedCode" class="applied-code">
              <i class="mdi mdi-tag-check-outline"></i>
              {{ appliedCode }} applied: KES {{ format(discountAmount) }} off
            </div>
            <div v-if="codeErr" class="code-err">{{ codeErr }}</div>
            <div class="row"><span>Discount</span><input type="number" v-model.number="discountAmount" :disabled="!!appliedCode" style="width:100px; text-align:right" /></div>
            <div v-if="attachedCustomer && attachedCustomer.loyalty_points > 0 && loyaltyRedeemRate > 0" class="row">
              <span>Use points</span>
              <div style="display:flex; gap:4px; align-items:center;">
                <input type="number" v-model.number="redeemPoints" min="0" :max="attachedCustomer.loyalty_points" style="width:80px; text-align:right" />
                <span class="muted small">/ {{ attachedCustomer.loyalty_points }}</span>
              </div>
            </div>
            <div v-if="loyaltyDiscount > 0" class="row applied-code" style="justify-content:space-between;">
              <span><i class="mdi mdi-star-outline"></i> Loyalty redemption</span>
              <span>−KES {{ format(loyaltyDiscount) }}</span>
            </div>
            <div class="row total"><span>Total</span><span>KES {{ format(total) }}</span></div>
          </div>
        </div>

        <div class="pay card">
          <h3>Take payment</h3>
          <div class="pay-method-row">
            <button :class="{'pay-method':1, active: paymentMethod==='cash'}"       @click="paymentMethod='cash'">Cash</button>
            <button :class="{'pay-method':1, active: paymentMethod==='mpesa_stk'}"  @click="paymentMethod='mpesa_stk'">M-Pesa STK</button>
            <button :class="{'pay-method':1, active: paymentMethod==='mpesa_till'}" @click="paymentMethod='mpesa_till'">M-Pesa (manual)</button>
            <button :class="{'pay-method':1, active: paymentMethod==='card'}"       @click="paymentMethod='card'">Card</button>
          </div>
          <div class="pay-fields">
            <div v-if="paymentMethod === 'cash'">
              <label>Amount tendered</label>
              <input type="number" v-model.number="tendered" />
              <div class="change">Change: KES {{ format(Math.max(0, tendered - total)) }}</div>
            </div>
            <div v-if="paymentMethod === 'mpesa_stk'">
              <label>Customer phone (STK will be sent)</label>
              <input v-model="phone" placeholder="0712345678" />
              <p class="muted small">(STK live send wired in Phase 2 — this scaffold marks as paid on click.)</p>
            </div>
            <div v-if="paymentMethod === 'mpesa_till'">
              <label>M-Pesa confirmation code</label>
              <input v-model="mpesaCode" placeholder="RGA1234ABC" />
            </div>
            <div v-if="paymentMethod === 'card'">
              <label>Card reference (from terminal)</label>
              <input v-model="cardRef" />
            </div>
          </div>
          <button class="primary full" :disabled="!canPay || processing" @click="checkout">
            {{ processing ? 'Processing…' : 'Complete sale (KES ' + format(total) + ')' }}
          </button>
          <p class="err" v-if="error">{{ error }}</p>
          <div v-if="lastReceipt" class="last-receipt">
            <p class="ok"><i class="mdi mdi-check-circle"></i> Sale recorded. Receipt: <strong>{{ lastReceipt }}</strong></p>
            <div class="receipt-actions">
              <button class="secondary small" @click="printReceipt(lastReceipt)"><i class="mdi mdi-printer"></i> Print receipt</button>
              <a :href="'/#/r/' + lastReceipt" target="_blank" class="secondary small link-btn"><i class="mdi mdi-open-in-new"></i> Digital copy</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RETURN modal -->
    <div v-if="showReturn" class="modal-back" @click.self="closeReturn">
      <div class="modal card">
        <h3>Process return</h3>
        <div v-if="!returnSale">
          <label>Receipt number</label>
          <input v-model="returnReceipt" placeholder="RCT-20260806-A4F2" ref="rctInput" />
          <div class="modal-actions">
            <button class="secondary" @click="closeReturn">Cancel</button>
            <button class="primary" @click="lookupReceipt" :disabled="!returnReceipt || returnLoading">
              {{ returnLoading ? 'Looking up…' : 'Find sale' }}
            </button>
          </div>
        </div>
        <div v-else>
          <div class="return-header">
            <div>
              <strong>{{ returnSale.receipt_no }}</strong>
              <span class="muted small">· {{ returnSale.created_at }} · Total KES {{ format(returnSale.total) }}</span>
            </div>
            <button class="secondary small" @click="returnSale = null">← Back</button>
          </div>
          <p class="muted small">Enter the quantity to return for each line, then choose a reason.</p>
          <table class="return-table">
            <thead><tr><th>Item</th><th class="r">Sold</th><th class="r">Return</th><th class="r">Value</th></tr></thead>
            <tbody>
              <tr v-for="it in returnSale.items" :key="it.id">
                <td>
                  <div>{{ it.variant_name_snapshot }}</div>
                  <div class="muted small">{{ it.sku_snapshot }} · KES {{ format(it.unit_price) }}</div>
                </td>
                <td class="r">{{ Math.round(it.quantity) }} <span v-if="it.returned_qty > 0" class="muted small">(-{{ Math.round(it.returned_qty) }})</span></td>
                <td class="r">
                  <input type="number" min="0"
                         :max="it.quantity - (it.returned_qty || 0)"
                         v-model.number="returnQtys[it.id]" style="width:70px; padding: 5px" />
                </td>
                <td class="r">KES {{ format((returnQtys[it.id] || 0) * it.unit_price) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="return-total">
            <span>Refund total</span><strong>KES {{ format(returnTotal) }}</strong>
          </div>
          <label>Reason (required)</label>
          <select v-model="returnReason">
            <option value="">-- select --</option>
            <option value="wrong size">Wrong size</option>
            <option value="faulty">Faulty / defective</option>
            <option value="changed mind">Customer changed mind</option>
            <option value="other">Other</option>
          </select>
          <label>Refund method</label>
          <select v-model="returnMethod">
            <option value="cash">Cash (from drawer)</option>
            <option value="mpesa_till">M-Pesa reversal</option>
            <option value="store_credit">Store credit</option>
          </select>
          <div class="modal-actions">
            <button class="secondary" @click="closeReturn">Cancel</button>
            <button class="danger" @click="submitReturn" :disabled="!canRefund || refunding">
              {{ refunding ? 'Processing…' : 'Refund KES ' + format(returnTotal) }}
            </button>
          </div>
          <p class="err" v-if="returnErr">{{ returnErr }}</p>
          <p class="ok"  v-if="returnOk">{{ returnOk }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can } from '@/api';
import { cacheProducts, loadCachedProducts, queueSale } from '@/offline/db';

export default {
  name: 'POSTerminal',
  components: { Navigation },
  data() {
    return {
      variants: [],
      search: '',
      cart: [],
      discountAmount: 0,
      discountCode: '', appliedCode: '', codeErr: '',
      // Customer + loyalty
      attachedCustomer: null,
      custSearch: '', custResults: [], custTimer: null,
      redeemPoints: 0,
      loyaltyRedeemRate: 0,   // KES per point, loaded from tenant settings
      paymentMethod: 'cash',
      tendered: 0,
      phone: '',
      mpesaCode: '',
      cardRef: '',
      processing: false,
      error: '',
      lastReceipt: '',
      // Return / refund flow
      showReturn: false,
      returnReceipt: '',
      returnLoading: false,
      returnSale: null,
      returnQtys: {},
      returnReason: '',
      returnMethod: 'cash',
      refunding: false,
      returnErr: '',
      returnOk: '',
      // STK polling
      stkId: null,
      stkPollInterval: null,
    };
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      let rows = this.variants;
      if (q) rows = rows.filter(v =>
        (v.product_name || '').toLowerCase().includes(q) ||
        (v.variant_name || '').toLowerCase().includes(q) ||
        (v.sku || '').toLowerCase() === q ||
        (v.barcode || '').toLowerCase() === q);
      return rows.slice(0, 60);
    },
    subtotal() { return this.cart.reduce((s, it) => s + (it.unit_price * it.quantity), 0); },
    loyaltyDiscount() {
      if (!this.attachedCustomer || !this.redeemPoints || this.loyaltyRedeemRate <= 0) return 0;
      const max = this.attachedCustomer.loyalty_points || 0;
      const pts = Math.max(0, Math.min(max, Number(this.redeemPoints) || 0));
      return Math.min(pts * this.loyaltyRedeemRate, Math.max(0, this.subtotal - (this.discountAmount || 0)));
    },
    total() { return Math.max(0, this.subtotal - (this.discountAmount || 0) - this.loyaltyDiscount); },
    canPay() {
      if (!this.cart.length) return false;
      if (this.paymentMethod === 'cash') return this.tendered >= this.total;
      if (this.paymentMethod === 'mpesa_till') return !!this.mpesaCode;
      if (this.paymentMethod === 'card') return !!this.cardRef;
      if (this.paymentMethod === 'mpesa_stk') return !!this.phone;
      return true;
    },
    returnTotal() {
      if (!this.returnSale) return 0;
      return this.returnSale.items.reduce((s, it) => s + ((this.returnQtys[it.id] || 0) * it.unit_price), 0);
    },
    canRefund() {
      return this.returnTotal > 0 && !!this.returnReason;
    },
  },
  async mounted() {
    await Promise.all([this.loadStock(), this.loadLoyaltyRate()]);
    this._onSyncChanged = (e) => {
      if (e.detail && e.detail.type === 'done' && e.detail.synced > 0) this.loadStock();
    };
    window.addEventListener('paysol-sync-changed', this._onSyncChanged);
  },
  beforeUnmount() {
    if (this._onSyncChanged) window.removeEventListener('paysol-sync-changed', this._onSyncChanged);
    if (this.stkPollInterval) clearInterval(this.stkPollInterval);
  },
  methods: {
    can(k) { return can(k); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async loadStock() {
      const shopId = parseInt(sessionStorage.getItem('defaultShopId') || '0', 10);
      if (navigator.onLine) {
        try {
          const { data } = await execute({ function: 'getStockLevels', shopId }, paysol);
          if (data.success) {
            this.variants = data.data;
            // Refresh offline cache in the background so we can keep selling if the network drops mid-shift
            try { await cacheProducts(data.data); } catch (e) { /* silent */ }
            return;
          }
        } catch (e) { /* fall through to offline cache */ }
      }
      // Offline (or online failed): try cache
      try {
        const cached = await loadCachedProducts();
        if (cached && cached.length) this.variants = cached;
      } catch (e) { /* silent */ }
    },
    async loadLoyaltyRate() {
      try {
        const { data } = await execute({ function: 'getLoyaltySettings' }, paysol);
        if (data.success && data.data && Number(data.data.loyalty_enabled) === 1) {
          this.loyaltyRedeemRate = Number(data.data.loyalty_redeem_rate) || 0;
        }
      } catch (e) { /* silent */ }
    },
    onCustSearch() {
      clearTimeout(this.custTimer);
      const q = this.custSearch.trim();
      if (q.length < 2) { this.custResults = []; return; }
      this.custTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listCustomers', search: q }, paysol);
        if (data.success) this.custResults = data.data.slice(0, 8);
      }, 200);
    },
    pickCustomer(c) {
      this.attachedCustomer = c;
      this.custResults = []; this.custSearch = '';
    },
    clearCustomer() {
      this.attachedCustomer = null;
      this.redeemPoints = 0;
    },
    onSearch() {
      const q = this.search.trim();
      if (!q) return;
      // Try instant add if exact barcode/SKU match
      const hit = this.variants.find(v => v.barcode === q || v.sku === q);
      if (hit) { this.addToCart(hit); this.search = ''; this.$refs.scan.focus(); }
    },
    addToCart(v) {
      const existing = this.cart.find(x => x.variant_id === v.variant_id);
      if (existing) existing.quantity += 1;
      else this.cart.push({
        variant_id: v.variant_id, product_name: v.product_name, variant_name: v.variant_name,
        unit_price: parseFloat(v.sell_price), quantity: 1
      });
    },
    async checkout() {
      this.error = ''; this.lastReceipt = ''; this.processing = true;
      const shopId = parseInt(sessionStorage.getItem('defaultShopId') || '0', 10);

      // Offline path — queue the sale to IndexedDB, sync when back online.
      // M-Pesa STK explicitly won't work offline (needs Daraja network).
      if (!navigator.onLine) {
        if (this.paymentMethod === 'mpesa_stk') {
          this.error = 'M-Pesa STK needs internet. Use Cash or Card while offline.';
          this.processing = false; return;
        }
        try {
          const payment = { method: this.paymentMethod, amount: this.total };
          if (this.paymentMethod === 'cash')       { payment.amount = this.tendered; payment.change = this.tendered - this.total; }
          if (this.paymentMethod === 'mpesa_till') { payment.reference = this.mpesaCode; payment.mpesaReceipt = this.mpesaCode; }
          if (this.paymentMethod === 'card')       { payment.reference = this.cardRef; }
          const payload = {
            function: 'recordSale', shopId,
            customerId: this.attachedCustomer ? this.attachedCustomer.id : 0,
            items: this.cart.map(c => ({ variantId: c.variant_id, quantity: c.quantity, unitPrice: c.unit_price })),
            payments: [payment],
            discountAmount: this.discountAmount || 0,
            discountCode: this.appliedCode || '',
            redeemPoints: this.redeemPoints || 0,
          };
          const row = await queueSale(payload);
          this.lastReceipt = row.local_receipt_no + ' (queued)';
          this.resetCart();
        } catch (e) {
          this.error = 'Failed to queue offline sale: ' + (e.message || 'unknown error');
        } finally { this.processing = false; }
        return;
      }

      // Live M-Pesa STK path
      if (this.paymentMethod === 'mpesa_stk') {
        try {
          const { data: stkRes } = await execute({
            function: 'posSaleStk',
            phone: this.phone, amount: this.total, reference: 'POS-' + Date.now(),
          }, paysol);
          if (!stkRes.success) { this.error = stkRes.message; this.processing = false; return; }
          this.stkId = stkRes.data.stkId;
          this.error = 'STK sent. Waiting for customer to complete on phone…';
          this.pollStk(shopId);
        } catch (e) {
          this.error = e.response?.data?.message || 'STK push failed.';
          this.processing = false;
        }
        return;
      }

      const payment = { method: this.paymentMethod, amount: this.total };
      if (this.paymentMethod === 'cash')       { payment.amount = this.tendered; payment.change = this.tendered - this.total; }
      if (this.paymentMethod === 'mpesa_till') { payment.reference = this.mpesaCode; payment.mpesaReceipt = this.mpesaCode; }
      if (this.paymentMethod === 'card')       { payment.reference = this.cardRef; }

      try {
        const { data } = await execute({
          function: 'recordSale',
          shopId,
          customerId: this.attachedCustomer ? this.attachedCustomer.id : 0,
          items: this.cart.map(c => ({ variantId: c.variant_id, quantity: c.quantity, unitPrice: c.unit_price })),
          payments: [payment],
          discountAmount: this.discountAmount || 0,
          discountCode: this.appliedCode || '',
          redeemPoints: this.redeemPoints || 0,
        }, paysol);
        if (!data.success) { this.error = data.message; return; }
        this.lastReceipt = data.data.receiptNo;
        this.resetCart();
        await this.loadStock();
      } catch (e) {
        this.error = e.response?.data?.message || 'Sale failed.';
      } finally { this.processing = false; }
    },
    resetCart() {
      this.cart = []; this.discountAmount = 0; this.tendered = 0;
      this.mpesaCode = ''; this.cardRef = ''; this.phone = '';
      this.discountCode = ''; this.appliedCode = ''; this.codeErr = '';
      this.attachedCustomer = null; this.redeemPoints = 0;
    },
    printReceipt(receiptNo) {
      // Open the public digital-receipt page in a new tab; user hits print (or we auto-print via query flag)
      const url = '/#/r/' + receiptNo + '?autoprint=1';
      window.open(url, '_blank', 'width=420,height=700');
    },
    clearCode() { this.discountCode = ''; this.appliedCode = ''; this.discountAmount = 0; this.codeErr = ''; },
    async tryApplyCode() {
      const code = (this.discountCode || '').trim().toUpperCase();
      this.codeErr = '';
      if (!code) { this.appliedCode = ''; return; }
      if (this.subtotal <= 0) { this.codeErr = 'Add items first.'; return; }
      try {
        const { data } = await execute({ function: 'validateDiscountCode', code, subtotal: this.subtotal }, paysol);
        if (!data.data || !data.data.valid) {
          this.codeErr = data.data && data.data.reason ? data.data.reason : (data.message || 'Invalid code.');
          this.appliedCode = ''; this.discountAmount = 0;
          return;
        }
        this.appliedCode = data.data.code;
        this.discountAmount = Number(data.data.amount);
      } catch (e) {
        this.codeErr = e.response?.data?.message || 'Failed to validate code.';
      }
    },
    pollStk(shopId) {
      const deadline = Date.now() + 90000;   // 90s soft timeout
      this.stkPollInterval = setInterval(async () => {
        if (Date.now() > deadline) {
          clearInterval(this.stkPollInterval); this.stkPollInterval = null; this.stkId = null;
          this.error = 'STK timed out. Customer did not respond — try again or use another payment method.';
          this.processing = false;
          return;
        }
        try {
          const { data } = await execute({ function: 'checkStkStatus', stkId: this.stkId }, paysol);
          if (!data.success) return;
          const s = data.data.status;
          if (s === 'paid') {
            clearInterval(this.stkPollInterval); this.stkPollInterval = null;
            // The callback already recorded the payment against a stub sale? No — with our current flow
            // there's no sale yet. So on 'paid', create the sale now with the mpesa_stk payment.
            const { data: rs } = await execute({
              function: 'recordSale',
              shopId,
              customerId: this.attachedCustomer ? this.attachedCustomer.id : 0,
              items: this.cart.map(c => ({ variantId: c.variant_id, quantity: c.quantity, unitPrice: c.unit_price })),
              payments: [{ method: 'mpesa_stk', amount: this.total, reference: 'STK-' + this.stkId, mpesaReceipt: data.data.mpesaReceipt, phone: this.phone }],
              discountAmount: this.discountAmount || 0,
              discountCode: this.appliedCode || '',
              redeemPoints: this.redeemPoints || 0,
            }, paysol);
            if (rs.success) {
              this.lastReceipt = rs.data.receiptNo;
              this.error = '';
              this.resetCart(); this.stkId = null;
              await this.loadStock();
            } else {
              this.error = 'Payment received but sale record failed: ' + rs.message;
            }
            this.processing = false;
          } else if (s === 'cancelled' || s === 'failed') {
            clearInterval(this.stkPollInterval); this.stkPollInterval = null; this.stkId = null;
            this.error = 'STK ' + s + '. ' + (data.data.resultDesc || '');
            this.processing = false;
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    },

    // -------- Returns --------
    closeReturn() {
      this.showReturn = false; this.returnSale = null; this.returnReceipt = '';
      this.returnQtys = {}; this.returnReason = ''; this.returnErr = ''; this.returnOk = '';
    },
    async lookupReceipt() {
      this.returnLoading = true; this.returnErr = '';
      try {
        const { data } = await execute({ function: 'lookupSaleByReceipt', receiptNo: this.returnReceipt.trim() }, paysol);
        if (!data.success) { this.returnErr = data.message; return; }
        this.returnSale = data.data;
        this.returnQtys = {};
        this.returnSale.items.forEach(it => this.returnQtys[it.id] = 0);
      } catch (e) { this.returnErr = e.response?.data?.message || 'Lookup failed.'; }
      finally { this.returnLoading = false; }
    },
    async submitReturn() {
      this.refunding = true; this.returnErr = ''; this.returnOk = '';
      const items = Object.entries(this.returnQtys)
        .filter(([, q]) => q > 0)
        .map(([saleItemId, quantity]) => ({ saleItemId: parseInt(saleItemId), quantity }));
      try {
        const { data } = await execute({
          function: 'refundSale',
          saleId: this.returnSale.id,
          reason: this.returnReason,
          method:  this.returnMethod,
          items,
        }, paysol);
        if (!data.success) { this.returnErr = data.message; return; }
        this.returnOk = 'Refund processed. Receipt: ' + data.data.refundReceiptNo + ' · KES ' + this.format(data.data.total);
        setTimeout(() => { this.closeReturn(); this.loadStock(); }, 2000);
      } catch (e) { this.returnErr = e.response?.data?.message || 'Refund failed.'; }
      finally { this.refunding = false; }
    },
  },
};
</script>

<style scoped>
.pos { display: grid; grid-template-columns: 1fr 420px; gap: 16px; padding: 16px; height: calc(100vh - 62px); }
.left { display: flex; flex-direction: column; min-height: 0; }
.search-row { margin-bottom: 12px; }
.search-row input { font-size: 16px; padding: 14px; }
.products-grid { flex: 1; overflow-y: auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; padding-right: 4px; }
.prod-tile {
  background: var(--paysol-card); border: 1px solid var(--paysol-line);
  border-radius: 8px; padding: 12px; cursor: pointer;
}
.prod-tile:hover { border-color: var(--paysol-teal); }
.prod-name { font-weight: 600; font-size: 13px; }
.prod-variant { color: var(--paysol-muted); font-size: 11px; margin: 3px 0 6px; }
.prod-price { color: var(--paysol-teal); font-weight: 700; }
.right { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.cart { flex: 1; overflow-y: auto; }
.pay { }
.empty { color: var(--paysol-muted); text-align: center; padding: 20px 0; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.totals { border-top: 1px solid var(--paysol-line); margin-top: 12px; padding-top: 10px; }
.totals .row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 13px; }
.totals .row.total { font-size: 16px; font-weight: 700; color: var(--paysol-teal); border-top: 1px solid var(--paysol-line); margin-top: 6px; padding-top: 10px; }
.pay-method-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin: 8px 0 12px; }
button.pay-method { background: var(--paysol-bg-elev); color: var(--paysol-muted); font-size: 11px; padding: 8px 4px; }
button.pay-method.active { background: var(--paysol-teal); color: #fff; }
.pay-fields { margin-bottom: 12px; }
.pay-fields label { margin-top: 8px; }
.change { color: var(--paysol-success); font-weight: 700; margin-top: 6px; }
button.full { width: 100%; padding: 14px; font-size: 15px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok  { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
button.small { padding: 4px 8px; font-size: 11px; }
.search-row { display: flex; gap: 8px; }
.search-row input { flex: 1; }
.return-btn { padding: 14px 18px; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 620px; max-height: 90vh; overflow-y: auto; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.return-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--paysol-line); }
.return-header strong { color: var(--paysol-teal); }
.return-table { margin: 10px 0; }
.return-total { display: flex; justify-content: space-between; padding: 12px 0; border-top: 1px solid var(--paysol-line); font-size: 16px; }
.return-total strong { color: var(--paysol-teal); }
.r { text-align: right; }
.cart-head { display: flex; justify-content: space-between; align-items: start; gap: 8px; margin-bottom: 8px; }
.cart-head h3 { margin: 0; }
.cust-picker { flex: 1; max-width: 260px; position: relative; }
.cust-search input { padding: 6px 10px; font-size: 12px; }
.cust-results { position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px; z-index: 20; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 6px; max-height: 220px; overflow-y: auto; box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.cust-result { padding: 6px 10px; cursor: pointer; font-size: 12px; border-bottom: 1px solid var(--paysol-line); }
.cust-result:hover { background: var(--paysol-teal-glow); }
.cust-chip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; background: var(--paysol-teal-glow); border-radius: 6px; color: var(--paysol-teal); font-size: 12px; }
.cust-chip strong { color: var(--paysol-text); font-weight: 600; }
.pts-badge { background: var(--paysol-teal); color: #fff; padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: 700; }
.cust-chip button { padding: 2px 6px; font-size: 12px; }
.last-receipt { padding: 10px 12px; background: var(--paysol-teal-glow); border-radius: 6px; margin-top: 8px; border-left: 3px solid var(--paysol-success); }
.last-receipt .ok { color: var(--paysol-success); font-size: 13px; margin: 0 0 6px; display: flex; align-items: center; gap: 4px; }
.last-receipt .ok strong { color: var(--paysol-text); font-family: 'JetBrains Mono', monospace; }
.receipt-actions { display: flex; gap: 6px; }
.receipt-actions button, .receipt-actions .link-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; font-size: 11px; text-decoration: none; border-radius: 4px; }
.link-btn { border: 1px solid var(--paysol-line); color: var(--paysol-text); background: transparent; font-weight: 600; }
.link-btn:hover { border-color: var(--paysol-teal); color: var(--paysol-teal); }
.applied-code { color: var(--paysol-teal); font-size: 11px; padding: 4px 8px; background: var(--paysol-teal-glow); border-radius: 4px; margin-top: 4px; display: inline-flex; align-items: center; gap: 4px; }
.applied-code i { font-size: 14px; }
.code-err { color: var(--paysol-danger); font-size: 11px; padding: 4px 0; }
</style>
